import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { COUNTRY_OPTIONS } from '../../lib/countries';

interface CountryDropdownProps {
  label?: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  label,
  value,
  onChange,
  defaultValue = 'HU',
  placeholder = 'Search or select country...',
  required = false,
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!value && defaultValue) {
      onChange(defaultValue);
    }
  }, [value, defaultValue, onChange]);

  const filteredOptions = COUNTRY_OPTIONS.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCountry = COUNTRY_OPTIONS.find(option => option.value === value);

  const handleSelect = (countryCode: string) => {
    onChange(countryCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-[#344054] mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div
        className={`
          relative w-full cursor-pointer
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
        `}
      >
        <div
          className={`
            w-full px-3.5 py-2.5 bg-white border rounded-lg text-left
            ${error ? 'border-red-500' : 'border-[#D0D5DD]'}
            ${!disabled && 'hover:border-[#7F56D9]'}
            ${isOpen && 'border-[#7F56D9] ring-2 ring-[#7F56D9]'}
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={`block truncate ${!selectedCountry ? 'text-[#667085]' : 'text-[#101828]'}`}>
            {selectedCountry ? selectedCountry.label : placeholder}
          </span>
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-[#EAECF0] rounded-lg shadow-lg">
            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#667085]" />
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full pl-9 pr-3 py-2 border border-[#D0D5DD] rounded-md focus:outline-none focus:ring-2 focus:ring-[#7F56D9] focus:border-[#7F56D9]"
                  placeholder="Search countries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            
            <div className="max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <button
                  key={option.value}
                  className={`
                    w-full px-3 py-2 text-left hover:bg-[#F9FAFB] transition-colors
                    ${option.value === value ? 'bg-[#F9F5FF] text-[#7F56D9]' : 'text-[#101828]'}
                  `}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-[#667085] text-center">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};